import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { resolveImageUrl } from "../lib/api";
import VehicleDetailModal from "../components/VehicleDetailModal";
import DriverPublicProfileModal from "../components/DriverPublicProfileModal";
import ReviewBadge from "../components/ReviewBadge";
import { VehicleStatusBadge, VehicleStatusStrip } from "../components/VehicleStatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Car, Loader2, MapPin, Share2, Search, X, Clock, SlidersHorizontal, ChevronDown, ChevronUp, LogIn, UserPlus, Eye } from "lucide-react";

const VEHICLE_TYPE_LABELS = { sedan: "Berline", suv: "SUV", "4x4": "4x4", minibus: "Minibus", pickup: "Pick-up" };
const CONDITION_LABELS = { neuf: "Neuf", bon_etat: "Bon etat", acceptable: "Acceptable" };
const FALLBACK_IMAGES = {
  sedan: "https://images.unsplash.com/photo-1622850780969-b7547252bccc?w=500&h=400&fit=crop",
  suv: "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=500&h=400&fit=crop",
  "4x4": "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=500&h=400&fit=crop",
  minibus: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?w=500&h=400&fit=crop",
  pickup: "https://images.unsplash.com/photo-1559416523-140ddc3d238c?w=500&h=400&fit=crop",
};
const CAMEROON_CITIES = ["Yaounde", "Douala", "Bafoussam", "Bamenda", "Garoua", "Maroua", "Ngaoundere", "Bertoua", "Ebolowa", "Kribi", "Limbe", "Buea"];

function getVehicleImageUrl(v) {
  const urls = v.image_urls || [];
  const firstUrl = urls[0] || v.image_url;
  if (firstUrl) { const r = resolveImageUrl(firstUrl); if (r && !r.includes("example.com")) return r; }
  return FALLBACK_IMAGES[v.vehicle_type] || FALLBACK_IMAGES.sedan;
}

function shareVehicleWhatsApp(e, v) {
  e.stopPropagation();
  // Use the backend /api/share/vehicle/<id> endpoint so WhatsApp/Facebook/Twitter
  // crawlers pick up the Open Graph meta tags and render a rich preview
  // (image, title, price). The endpoint auto-redirects real users to the SPA.
  const apiUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const shareUrl = `${apiUrl}/api/share/vehicle/${v.id}`;
  const text = `*TrigoTour · Véhicule en location*\n\n${v.brand} ${v.model} (${v.year})\n${v.city ? `Ville: ${v.city}\n` : ""}Prix: ${v.price_per_day?.toLocaleString()} XAF/jour\n\nDécouvrez-le : ${shareUrl}`;
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
}

function ChipFilter({ label, active, onClick, testId }) {
  return (
    <button onClick={onClick} className={`shrink-0 px-5 py-2.5 rounded-full text-[13px] font-semibold transition-all ${active ? "bg-[#0A2540] text-white shadow-sm" : "bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[#0A2540] hover:text-[#0A2540]"}`} data-testid={testId} style={{ fontFamily: "Outfit" }}>
      {label}
    </button>
  );
}

export default function VehiclesPage() {
  const { user } = useAuth();
  const isExplorer = !user;
  const handleCardClick = (v) => {
    // Both explorer and logged-in users open the detail modal.
    // In explorer mode the modal is rendered in read-only preview mode with
    // a "Se connecter pour réserver" CTA instead of the rent action.
    setSelectedVehicle(v);
  };
  const [searchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [searchText, setSearchText] = useState("");
  // Advanced filters
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filterBrand, setFilterBrand] = useState("");
  const [filterCondition, setFilterCondition] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [filterAC, setFilterAC] = useState(false);
  const [filterFuelIncluded, setFilterFuelIncluded] = useState(false);
  const [filterMinSeats, setFilterMinSeats] = useState("");
  const [sortBy, setSortBy] = useState("relevance");

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/vehicles"); setVehicles(data); }
    catch (e) { if (process.env.NODE_ENV === "development") console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  useEffect(() => {
    // Support ?vehicle=<id> (from shared WhatsApp links) and legacy ?highlight=<id>.
    // If the vehicle isn't in the loaded list (e.g. filtered out or on another
    // page), fetch it directly by ID so visitors coming from a shared link
    // always see the preview modal.
    const vehicleId = searchParams.get("vehicle") || searchParams.get("highlight");
    if (!vehicleId || selectedVehicle || loading) return;
    const found = vehicles.find(v => v.id === vehicleId);
    if (found) { setSelectedVehicle(found); return; }
    // Fallback : fetch individually (works for guests, public endpoint).
    let cancelled = false;
    api.get(`/vehicles/${vehicleId}`)
      .then(({ data }) => { if (!cancelled) setSelectedVehicle(data); })
      .catch(() => { /* silently ignore invalid ids */ });
    return () => { cancelled = true; };
  }, [searchParams, vehicles, selectedVehicle, loading]);

  const availableCities = useMemo(() => [...new Set(vehicles.map(v => v.city).filter(Boolean))].sort(), [vehicles]);
  const availableBrands = useMemo(() => [...new Set(vehicles.map(v => v.brand).filter(Boolean))].sort(), [vehicles]);

  const filteredVehicles = useMemo(() => {
    const minP = minPrice ? parseInt(minPrice) : 0;
    const maxP = maxPrice ? parseInt(maxPrice) : Infinity;
    const minS = filterMinSeats ? parseInt(filterMinSeats) : 0;
    const list = vehicles.filter(v => {
      if (filterType && v.vehicle_type !== filterType) return false;
      if (filterCity && (v.city || "") !== filterCity) return false;
      if (filterBrand && v.brand !== filterBrand) return false;
      if (filterCondition && v.vehicle_condition !== filterCondition) return false;
      if (filterAC && !v.air_conditioning) return false;
      if (filterFuelIncluded && v.fuel_policy !== "included") return false;
      const price = v.price_per_day || 0;
      if (price < minP || price > maxP) return false;
      if (minS && (v.seats || 0) < minS) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        const haystack = `${v.brand} ${v.model} ${v.color} ${v.city || ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    // Sort
    if (sortBy === "price_asc") list.sort((a, b) => (a.price_per_day || 0) - (b.price_per_day || 0));
    else if (sortBy === "price_desc") list.sort((a, b) => (b.price_per_day || 0) - (a.price_per_day || 0));
    else if (sortBy === "rating") list.sort((a, b) => (b.owner_rating || 0) - (a.owner_rating || 0));
    else if (sortBy === "newest") list.sort((a, b) => (b.year || 0) - (a.year || 0));
    return list;
  }, [vehicles, filterType, filterCity, filterBrand, filterCondition, filterAC, filterFuelIncluded, minPrice, maxPrice, filterMinSeats, sortBy, searchText]);

  const types = [
    { id: "", label: "Toutes" }, { id: "sedan", label: "Berline" }, { id: "suv", label: "SUV" },
    { id: "minibus", label: "Bus" }, { id: "4x4", label: "4x4" }, { id: "pickup", label: "Pick-up" },
  ];

  const hasFilters = filterType || filterCity || searchText || filterBrand || filterCondition || filterAC || filterFuelIncluded || minPrice || maxPrice || filterMinSeats || sortBy !== "relevance";

  const clearAll = () => {
    setFilterType(""); setFilterCity(""); setSearchText("");
    setFilterBrand(""); setFilterCondition(""); setFilterAC(false); setFilterFuelIncluded(false);
    setMinPrice(""); setMaxPrice(""); setFilterMinSeats(""); setSortBy("relevance");
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] pb-20 sm:pb-0" style={{ fontFamily: 'Outfit, Manrope, sans-serif' }} data-testid="vehicles-page">
      {/* Header */}
      <div className="bg-[#F5F7FA] sticky top-[72px] z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          {/* Title */}
          <div className="flex items-center gap-3 mb-4">
            <Link
              to={user ? "/passenger" : "/welcome"}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-[#E2E8F0] text-[#0A2540] hover:bg-[#0A2540] hover:text-white transition"
              aria-label="Retour"
              data-testid="vehicles-back"
            >
              <ChevronLeftIcon />
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540] leading-tight" style={{ fontFamily: 'Outfit' }}>
                Location
              </h1>
              <p className="text-[13px] text-[#64748B] mt-0.5">Véhicule avec chauffeur professionnel</p>
            </div>
            {isExplorer && (
              <span
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#FACC15]/20 border border-[#FACC15] text-[#0A2540] text-[11px] font-extrabold uppercase tracking-widest px-3 py-1.5"
                data-testid="explorer-badge"
              >
                <Eye className="w-3 h-3" /> Mode Explorer
              </span>
            )}
          </div>

          {isExplorer && (
            <div
              className="mb-4 rounded-2xl bg-gradient-to-r from-[#0A2540] to-[#0F2E4E] text-white p-4 flex items-center gap-3"
              data-testid="explorer-banner"
            >
              <div className="w-10 h-10 rounded-full bg-[#FACC15] flex items-center justify-center shrink-0">
                <Eye className="w-4 h-4 text-[#0A2540]" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-extrabold" style={{ fontFamily: "Outfit" }}>
                  Vous consultez en mode Explorer
                </p>
                <p className="text-[11px] text-white/70">
                  Créez un compte pour réserver un véhicule.
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition"
                  data-testid="explorer-login-btn"
                >
                  <LogIn className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Se connecter</span>
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-[#FACC15] hover:bg-[#EAB308] text-[#0A2540] text-xs font-extrabold transition"
                  data-testid="explorer-register-btn"
                >
                  <UserPlus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Créer un compte</span>
                </Link>
              </div>
            </div>
          )}

          {/* Search bar (compact) */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Rechercher par marque, modèle, ville..." className="pl-10 h-11 rounded-full border-[#E2E8F0] text-sm bg-white focus:bg-white" data-testid="vehicle-search-input" />
            </div>
            <button onClick={() => setShowAdvanced(s => !s)} className="shrink-0 h-11 w-11 rounded-full bg-white border border-[#E2E8F0] text-[#0A2540] flex items-center justify-center hover:bg-[#0A2540] hover:text-white transition" data-testid="toggle-advanced-filters" aria-label="Filtres">
              <SlidersHorizontal className="w-4 h-4" />
            </button>
          </div>

          {/* Type pill filters — big rounded black-active */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
            {types.map(t => (
              <ChipFilter key={t.id} label={t.label} active={filterType === t.id} onClick={() => setFilterType(filterType === t.id ? "" : t.id)} testId={`filter-${t.id || "all"}`} />
            ))}
          </div>

          {/* City filters (secondary row) */}
          {availableCities.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pt-2 -mx-1 px-1 scrollbar-hide">
              {availableCities.map(c => (
                <button
                  key={c}
                  onClick={() => setFilterCity(filterCity === c ? "" : c)}
                  className={`shrink-0 px-3.5 py-1.5 rounded-full text-[12px] font-medium border transition ${filterCity === c ? "bg-[#FACC15] text-[#0A2540] border-[#FACC15]" : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#0A2540]"}`}
                  data-testid={`filter-city-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {/* Advanced filters panel */}
          {showAdvanced && (
            <div className="mt-4 p-4 rounded-2xl bg-white border border-[#E2E8F0] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="advanced-filters-panel">
              <div>
                <label className="text-[10px] font-semibold text-[#64748B] uppercase mb-1 block">Marque</label>
                <Select value={filterBrand || "_all"} onValueChange={v => setFilterBrand(v === "_all" ? "" : v)}>
                  <SelectTrigger className="h-9 text-xs bg-white" data-testid="filter-brand"><SelectValue placeholder="Toutes" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Toutes</SelectItem>
                    {availableBrands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-[#64748B] uppercase mb-1 block">État</label>
                <Select value={filterCondition || "_all"} onValueChange={v => setFilterCondition(v === "_all" ? "" : v)}>
                  <SelectTrigger className="h-9 text-xs bg-white" data-testid="filter-condition"><SelectValue placeholder="Tous" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tous</SelectItem>
                    <SelectItem value="neuf">Neuf</SelectItem>
                    <SelectItem value="bon_etat">Bon état</SelectItem>
                    <SelectItem value="acceptable">Acceptable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-[#64748B] uppercase mb-1 block">Prix min (XAF/j)</label>
                <Input type="number" value={minPrice} onChange={e => setMinPrice(e.target.value)} placeholder="Ex: 15000" className="h-9 text-xs bg-white" data-testid="filter-min-price" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-[#64748B] uppercase mb-1 block">Prix max (XAF/j)</label>
                <Input type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} placeholder="Ex: 80000" className="h-9 text-xs bg-white" data-testid="filter-max-price" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-[#64748B] uppercase mb-1 block">Places min</label>
                <Select value={filterMinSeats || "_any"} onValueChange={v => setFilterMinSeats(v === "_any" ? "" : v)}>
                  <SelectTrigger className="h-9 text-xs bg-white" data-testid="filter-min-seats"><SelectValue placeholder="Indifférent" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_any">Indifférent</SelectItem>
                    <SelectItem value="4">4+</SelectItem>
                    <SelectItem value="5">5+</SelectItem>
                    <SelectItem value="7">7+</SelectItem>
                    <SelectItem value="9">9+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-[#64748B] uppercase mb-1 block">Trier par</label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="h-9 text-xs bg-white" data-testid="filter-sort"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">Pertinence</SelectItem>
                    <SelectItem value="price_asc">Prix croissant</SelectItem>
                    <SelectItem value="price_desc">Prix décroissant</SelectItem>
                    <SelectItem value="rating">Mieux notés</SelectItem>
                    <SelectItem value="newest">Plus récents</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2 lg:col-span-2 justify-end pt-1">
                <label className="flex items-center gap-2 cursor-pointer text-xs text-[#0A2540]" data-testid="filter-ac-label">
                  <input type="checkbox" checked={filterAC} onChange={e => setFilterAC(e.target.checked)} className="w-4 h-4 accent-[#0A2540]" data-testid="filter-ac" />
                  Climatisation
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-xs text-[#0A2540]" data-testid="filter-fuel-label">
                  <input type="checkbox" checked={filterFuelIncluded} onChange={e => setFilterFuelIncluded(e.target.checked)} className="w-4 h-4 accent-[#0A2540]" data-testid="filter-fuel" />
                  Carburant inclus
                </label>
                {hasFilters && (
                  <button onClick={clearAll} className="text-xs text-[#EF4444] flex items-center gap-1 mt-1" data-testid="clear-filters">
                    <X className="w-3.5 h-3.5" /> Effacer les filtres
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {hasFilters && <p className="text-sm text-[#64748B] mb-3">{filteredVehicles.length} véhicule(s) trouvé(s)</p>}

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
        ) : filteredVehicles.length === 0 ? (
          <div className="text-center py-24">
            <Car className="w-14 h-14 mx-auto text-gray-200 mb-4" />
            <p className="text-gray-500 text-lg" style={{ fontFamily: 'Outfit' }}>Aucun véhicule ne correspond</p>
            {hasFilters && <button onClick={() => { setFilterType(""); setFilterCity(""); setSearchText(""); }} className="text-sm text-[#0A2540] underline mt-3">Voir tous les véhicules</button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredVehicles.map((v) => {
              const rentalTags = [];
              if ((v.price_per_day || 0) > 0) rentalTags.push("Jour");
              if ((v.price_per_hour || 0) > 0) rentalTags.push("Heure");
              if ((v.price_city_tour || 0) > 0) rentalTags.push("Tour ville");
              if ((v.price_out_of_city || 0) > 0) rentalTags.push("Sortie");
              return (
                <div
                  key={v.id}
                  className="relative group cursor-pointer bg-white rounded-3xl border border-[#E2E8F0] overflow-hidden hover:border-[#0A2540]/40 hover:shadow-md transition"
                  onClick={() => handleCardClick(v)}
                  data-testid={`vehicle-card-${v.id}`}
                >
                  <VehicleStatusStrip status={v.operational_status} />
                  {/* Image */}
                  <div className="relative bg-[#F1F5F9]">
                    <img src={getVehicleImageUrl(v)} alt={`${v.brand} ${v.model}`} className="w-full h-56 object-cover" onError={(e) => { e.target.src = FALLBACK_IMAGES[v.vehicle_type] || FALLBACK_IMAGES.sedan; }} />
                    {/* Status badge overlay */}
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 items-center">
                      <VehicleStatusBadge status={v.operational_status} />
                      {rentalTags.map((t) => (
                        <span key={t} className="text-[10px] font-bold uppercase tracking-wide bg-[#0A2540]/85 backdrop-blur-sm text-[#FACC15] rounded-full px-2.5 py-1">
                          {t}
                        </span>
                      ))}
                    </div>
                    <button
                      onClick={(e) => shareVehicleWhatsApp(e, v)}
                      className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center hover:bg-white shadow-sm"
                      data-testid={`share-vehicle-${v.id}`}
                    >
                      <Share2 className="w-4 h-4 text-[#25D366]" />
                    </button>
                  </div>
                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-[15px] font-extrabold text-[#0A2540] truncate" style={{ fontFamily: 'Outfit' }}>
                          {v.brand} {v.model} · {v.year}
                        </h3>
                        <p className="text-xs text-[#64748B] mt-1 flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{v.city || "N/A"}</span>
                          <span className="w-1 h-1 rounded-full bg-[#CBD5E1]" />
                          <span className="inline-flex items-center gap-1">👥 {v.seats || 4} places</span>
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] uppercase font-bold text-[#94A3B8] tracking-wider">À partir de</p>
                        <p className="text-xl font-extrabold text-[#0A2540] leading-tight" style={{ fontFamily: 'Outfit' }}>
                          {(v.price_per_day || v.price_per_hour || v.price_city_tour || 0).toLocaleString("fr-FR")}
                        </p>
                        <p className="text-[10px] font-semibold text-[#64748B]">FCFA</p>
                      </div>
                    </div>
                    {/* Feature chips */}
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {v.air_conditioning && (
                        <span className="text-[11px] text-[#0A2540] bg-[#F1F5F9] rounded-full px-2.5 py-1">Climatisation</span>
                      )}
                      {v.has_music && (
                        <span className="text-[11px] text-[#0A2540] bg-[#F1F5F9] rounded-full px-2.5 py-1">Musique</span>
                      )}
                      {v.has_charger && (
                        <span className="text-[11px] text-[#0A2540] bg-[#F1F5F9] rounded-full px-2.5 py-1">Chargeur</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <VehicleDetailModal vehicle={selectedVehicle} open={!!selectedVehicle} onClose={() => setSelectedVehicle(null)} showRentButton={!!user} />
      <DriverPublicProfileModal driverId={selectedDriverId} open={!!selectedDriverId} onClose={() => setSelectedDriverId(null)} />
    </div>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  );
}
